// 时令：节气、节日与剪纸地景的场景（DESIGN.md §6）。纯函数，不依赖 Taro，脚本可直接校验。
// 只用真实的日历事实：节气按天文近似公式推，春节 / 中秋按农历查表；表外年份不猜，退回四季。

/** 二十四节气，从小寒起（公历一月第一个节气）。 */
export const SOLAR_TERMS = [
  "小寒", "大寒", "立春", "雨水", "惊蛰", "春分", "清明", "谷雨", "立夏", "小满", "芒种", "夏至",
  "小暑", "大暑", "立秋", "处暑", "白露", "秋分", "寒露", "霜降", "立冬", "小雪", "大雪", "冬至"
] as const;

// 21 世纪「寿星」通式：日 = ⌊Y·D + C⌋ − ⌊L/4⌋，Y 为年份后两位，D = 0.2422；
// 小寒、大寒、立春、雨水用 L = Y − 1，其余用 L = Y。
const C = [5.4055, 20.12, 3.87, 18.73, 5.63, 20.646, 4.81, 20.1, 5.52, 21.04, 5.678, 21.37, 7.108, 22.83, 7.5, 23.13, 7.646, 23.042, 8.318, 23.438, 7.438, 22.36, 7.18, 21.94];
/** 通式的已知例外（年-节气序号 → 修正天数）。本表只收 2000–2099 里公认的几处。 */
const EXCEPTIONS: Record<string, number> = { "2008-9": 1, "2016-12": 1, "2002-14": 1, "2026-3": -1, "2084-5": 1, "2089-19": 1, "2089-20": 1, "2082-1": 1 };

/** 第 index 个节气（0 = 小寒）在 year 年的公历日。仅 2001–2099。 */
export function solarTermDay(year: number, index: number): { month: number; day: number } {
  const y = year % 100;
  const leapBase = index < 4 ? y - 1 : y;
  const day = Math.floor(y * 0.2422 + C[index]) - Math.floor(leapBase / 4) + (EXCEPTIONS[`${year}-${index}`] ?? 0);
  return { month: Math.floor(index / 2) + 1, day };
}

const key = (m: number, d: number) => m * 100 + d;

/** 当天所在的节气：当天正是节气日，或上一个已经过去的节气。 */
export function solarTermOf(date: Date): { name: string; index: number; today: boolean } {
  const year = date.getFullYear();
  const now = key(date.getMonth() + 1, date.getDate());
  let found = { name: "冬至", index: 23, today: false }; // 一月小寒之前，仍在上一年冬至里
  for (let i = 0; i < 24; i++) {
    const t = solarTermDay(year, i);
    const k = key(t.month, t.day);
    if (k <= now) found = { name: SOLAR_TERMS[i], index: i, today: k === now };
  }
  return found;
}

/** 「今日秋分」或「时值秋分」。 */
export function termPhrase(date: Date): string {
  const term = solarTermOf(date);
  return term.today ? `今日${term.name}` : `时值${term.name}`;
}

/** 早上好 / 中午好 / 下午好 / 晚上好；深夜不说「早上好」，只说「您好」。 */
export function greeting(date: Date): string {
  const h = date.getHours();
  if (h >= 5 && h < 11) return "早上好";
  if (h >= 11 && h < 13) return "中午好";
  if (h >= 13 && h < 18) return "下午好";
  if (h >= 18 && h < 24) return "晚上好";
  return "您好";
}

// 农历节日的公历日（查表，不在客户端算农历）。表外年份不出节日场景。
const SPRING_FESTIVAL: Record<number, string> = {
  2025: "01-29", 2026: "02-17", 2027: "02-06", 2028: "01-26", 2029: "02-13", 2030: "02-03",
  2031: "01-23", 2032: "02-11", 2033: "01-31", 2034: "02-19", 2035: "02-08"
};
const MID_AUTUMN: Record<number, string> = {
  2025: "10-06", 2026: "09-25", 2027: "09-15", 2028: "10-03", 2029: "09-22", 2030: "09-12",
  2031: "10-01", 2032: "09-19", 2033: "09-08", 2034: "09-27", 2035: "09-16"
};

export type Scene = "spring" | "summer" | "autumn" | "winter" | "spring-festival" | "mid-autumn" | "national-day";
export type Season = "spring" | "summer" | "autumn" | "winter";

const DAY = 86_400_000;
function within(date: Date, year: number, mmdd: string | undefined, before: number, after: number): boolean {
  if (!mmdd) return false;
  const [m, d] = mmdd.split("-").map(Number);
  const center = new Date(year, m - 1, d).getTime();
  const today = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return today >= center - before * DAY && today <= center + after * DAY;
}

/**
 * 地景场景：节日优先（春节 > 中秋 > 国庆），否则按节气分四季——
 * 立春至谷雨为春，立夏至大暑为夏，立秋至霜降为秋，立冬至大寒为冬。按日期确定，不随机、不轮播。
 */
export function sceneOf(date: Date): Scene {
  const year = date.getFullYear();
  // 春节：除夕到元宵（初一前一天到初一后十四天）；跨年的除夕也要认得上一年表。
  if (within(date, year, SPRING_FESTIVAL[year], 1, 14) || within(date, year + 1, SPRING_FESTIVAL[year + 1], 1, 14)) return "spring-festival";
  if (within(date, year, MID_AUTUMN[year], 2, 1)) return "mid-autumn";
  if (date.getMonth() === 9 && date.getDate() <= 7) return "national-day";
  const { index } = solarTermOf(date);
  if (index >= 2 && index <= 7) return "spring";
  if (index >= 8 && index <= 13) return "summer";
  if (index >= 14 && index <= 19) return "autumn";
  return "winter";
}

/** 天光四季：保留七场景的节日优先级，春节归冬，中秋与国庆归秋。 */
export function seasonOf(date: Date): Season {
  const scene = sceneOf(date);
  if (scene === "spring-festival") return "winter";
  if (scene === "mid-autumn" || scene === "national-day") return "autumn";
  return scene;
}

const WEEK = ["日", "一", "二", "三", "四", "五", "六"];
/** 「时值秋分 · 9 月 26 日 星期六」——会员首页问候下的一行，只说真实的日子。 */
export function dayLine(date: Date): string {
  return `${termPhrase(date)} · ${date.getMonth() + 1} 月 ${date.getDate()} 日 星期${WEEK[date.getDay()]}`;
}
